import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const RevisionReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/listings/${id}`);
      setListing(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load listing',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const approveRevision = async () => {
    try {
      const response = await apiCall(`/api/admin/listings/${id}/review-revision`, {
        method: 'POST',
        body: JSON.stringify({ action: 'approve' })
      });
      
      if (response.success) {
        toast({
          title: 'Success',
          description: 'Revision approved. Live listing updated.'
        });
        navigate('/admin/approvals');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const rejectRevision = async () => {
    if (!rejectionReason.trim()) {
      toast({
        title: 'Error',
        description: 'Rejection reason is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await apiCall(`/api/admin/listings/${id}/review-revision`, {
        method: 'POST',
        body: JSON.stringify({ 
          action: 'reject',
          rejection_reason: rejectionReason
        })
      });
      
      if (response.success) {
        toast({
          title: 'Revision Rejected',
          description: 'Vendor has been notified'
        });
        navigate('/admin/approvals');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const renderDiffField = (label, liveValue, revisionValue) => {
    const hasChanged = liveValue !== revisionValue;
    
    return (
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-sm font-semibold text-gray-600">{label} (Live)</Label>
          <div className={`p-3 mt-1 border rounded-md ${hasChanged ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
            {liveValue || <span className="text-gray-400">Not set</span>}
          </div>
        </div>
        <div>
          <Label className="text-sm font-semibold text-gray-600">{label} (Proposed)</Label>
          <div className={`p-3 mt-1 border rounded-md ${hasChanged ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
            {revisionValue || <span className="text-gray-400">Not set</span>}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading revision...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!listing || !listing.revision_data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">No revision data found</p>
        </div>
      </DashboardLayout>
    );
  }

  const liveData = listing.data || {};
  const revisionData = listing.revision_data.data || {};

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/approvals')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Approvals
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Revision Review</h1>
              <p className="text-gray-600">Compare live version with proposed changes</p>
            </div>
          </div>
          <Badge className="bg-blue-600">Active Listing - Pending Revision</Badge>
        </div>

        {/* Status Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-900">This listing is currently ACTIVE and visible to customers</p>
                <p className="text-sm text-blue-700 mt-1">
                  The vendor has submitted changes for your review. The current live version will remain unchanged until you approve the revision.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Critical Changes Summary */}
        {(() => {
          const criticalChanges = [];
          
          // Check title
          if (listing.title !== listing.revision_data.title) {
            criticalChanges.push('Title');
          }
          
          // Check main image
          if ((listing.media || [])[0] !== (listing.revision_data.media || [])[0]) {
            criticalChanges.push('Main Image');
          }
          
          // Check category
          if (listing.category !== listing.revision_data.category) {
            criticalChanges.push('Category');
          }
          
          // Check price fields
          if (liveData.priceType !== revisionData.priceType) {
            criticalChanges.push('Price Type');
          }
          if (liveData.hourlyRate !== revisionData.hourlyRate) {
            criticalChanges.push('Hourly Rate');
          }
          if (liveData.dailyRate !== revisionData.dailyRate) {
            criticalChanges.push('Daily Rate');
          }
          if (liveData.price !== revisionData.price) {
            criticalChanges.push('Price');
          }
          
          // Check tax
          if (liveData.chargeTax !== revisionData.chargeTax) {
            criticalChanges.push('Tax Settings');
          }
          if (listing.hsn_sac_code !== listing.revision_data.hsn_sac_code) {
            criticalChanges.push('HSN/SAC Code');
          }
          
          return criticalChanges.length > 0 ? (
            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-orange-900">Critical Changes Detected</p>
                    <p className="text-sm text-orange-700 mt-1">
                      The following critical fields have been modified and require your approval:
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {criticalChanges.map((change, idx) => (
                        <Badge key={change.id || change.name || `${change.title || "change"}-${idx}`} className="bg-orange-600 text-white">
                          {change}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null;
        })()}

        {/* Side-by-Side Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Changes Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Title */}
              {renderDiffField('Title', listing.title, listing.revision_data.title)}
              
              {/* Images - CRITICAL FIELD */}
              <div>
                <Label className="text-sm font-semibold text-gray-600 mb-2 block">
                  Images {((listing.media || [])[0] !== (listing.revision_data.media || [])[0]) && 
                    <span className="text-red-600 font-bold ml-2">⚠️ MAIN IMAGE CHANGED (Critical)</span>
                  }
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Live Images</Label>
                    <div className={`mt-2 p-3 border rounded-md ${
                      JSON.stringify(listing.media) !== JSON.stringify(listing.revision_data.media) 
                        ? 'bg-red-50 border-red-200' 
                        : 'bg-gray-50'
                    }`}>
                      {listing.media && listing.media.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2">
                          {listing.media.map((img, idx) => (
                            <div key={img.id || img.name || `${img.title || "img"}-${idx}`} className="relative">
                              <img 
                                src={(() => { const u = typeof img === 'string' ? img : (img?.cdn_url || img?.url || ''); return u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`; })()}
                                alt={`Live ${idx + 1}`}
                                className="w-full h-24 object-cover rounded border"
                              />
                              {idx === 0 && (
                                <div className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-2 py-0.5 rounded">
                                  Main
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">No images</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Proposed Images</Label>
                    <div className={`mt-2 p-3 border rounded-md ${
                      JSON.stringify(listing.media) !== JSON.stringify(listing.revision_data.media) 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-50'
                    }`}>
                      {listing.revision_data.media && listing.revision_data.media.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2">
                          {listing.revision_data.media.map((img, idx) => (
                            <div key={img.id || img.name || `${img.title || "img"}-${idx}`} className="relative">
                              <img 
                                src={(() => { const u = typeof img === 'string' ? img : (img?.cdn_url || img?.url || ''); return u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`; })()}
                                alt={`Proposed ${idx + 1}`}
                                className="w-full h-24 object-cover rounded border"
                              />
                              {idx === 0 && (
                                <div className="absolute top-1 left-1 bg-green-600 text-white text-xs px-2 py-0.5 rounded">
                                  Main
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">No images</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Description */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Description (Live)</Label>
                  <div className={`p-3 mt-1 border rounded-md ${listing.description !== listing.revision_data.description ? 'bg-red-50 border-red-200' : 'bg-gray-50'} max-h-40 overflow-y-auto`}>
                    {listing.description || <span className="text-gray-400">Not set</span>}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Description (Proposed)</Label>
                  <div className={`p-3 mt-1 border rounded-md ${listing.description !== listing.revision_data.description ? 'bg-green-50 border-green-200' : 'bg-gray-50'} max-h-40 overflow-y-auto`}>
                    {listing.revision_data.description || <span className="text-gray-400">Not set</span>}
                  </div>
                </div>
              </div>

              {/* Category */}
              {renderDiffField('Category', listing.category, listing.revision_data.category)}
              
              {/* Price Fields */}
              {renderDiffField('Price Type', liveData.priceType, revisionData.priceType)}
              {renderDiffField('Hourly Rate', liveData.hourlyRate, revisionData.hourlyRate)}
              {renderDiffField('Daily Rate', liveData.dailyRate, revisionData.dailyRate)}
              {renderDiffField('Fixed Price', liveData.price, revisionData.price)}
              
              {/* Location */}
              {renderDiffField('Location Type', liveData.locationType, revisionData.locationType)}
              
              {/* Tax */}
              {renderDiffField('Charge Tax', liveData.chargeTax ? 'Yes' : 'No', revisionData.chargeTax ? 'Yes' : 'No')}
              {renderDiffField('HSN/SAC Code', listing.hsn_sac_code, listing.revision_data.hsn_sac_code)}
            </div>
          </CardContent>
        </Card>

        {/* Action Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Review Actions</CardTitle>
          </CardHeader>
          <CardContent>
            {!showRejectForm ? (
              <div className="flex gap-3">
                <Button
                  onClick={approveRevision}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Changes - Update Live Listing
                </Button>
                <Button
                  onClick={() => setShowRejectForm(true)}
                  variant="destructive"
                  className="flex-1"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject Changes
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Rejection Reason (Required)</Label>
                  <Textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a clear reason for rejection..."
                    className="mt-2"
                    rows={4}
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={rejectRevision}
                    variant="destructive"
                    className="flex-1"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Confirm Rejection
                  </Button>
                  <Button
                    onClick={() => setShowRejectForm(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vendor Info */}
        <Card>
          <CardHeader>
            <CardTitle>Vendor Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <p><span className="font-semibold">Vendor:</span> {listing.vendor_name}</p>
              <p><span className="font-semibold">Last Updated:</span> {new Date(listing.updated_at).toLocaleString()}</p>
              <p><span className="font-semibold">Listing Type:</span> {listing.listing_type}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default RevisionReview;
